import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Table } from "react-bootstrap";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";
import DocumentFormats from "./DocumentFormats";

const GROUP_TITLES = {
  CASE: "Case details",
  BORROWER: "Borrower details (one Excel row per borrower)",
  LOAN: "Loan details",
  VEHICLE: "Vehicle details",
};

const DEFAULT_DATE_FORMAT = "DD-MM-YYYY";

const simplify = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// The bank's column for one item, always picked from a list (no typing mistakes): the columns of the bank's Excel
// chosen in Step 1, plus the names already in the setup (saved or copied), so a product can be edited without the file
const ColumnPicker = ({ value, choices, fromExcel, usedFor, itemLabel, onChange }) => {
  if (!choices.length) {
    return (
      <Form.Select size="sm" disabled style={{ fontSize: "12px" }}>
        <option>Choose the bank's Excel in Step 1 first</option>
      </Form.Select>
    );
  }
  // Only an Excel chosen in Step 1 shows which saved names it doesn't have
  const missing = fromExcel && value.trim() && !fromExcel.some((h) => simplify(h) === simplify(value));
  return (
    <>
      <Form.Select size="sm" value={value} onChange={(e) => onChange(e.target.value)} isInvalid={Boolean(missing)} style={{ fontSize: "12px" }}>
        <option value="">— The bank doesn't send this —</option>
        {choices.map((h) => {
          const other = usedFor.get(simplify(h));
          return (
            <option key={h} value={h}>
              {h}
              {other && other !== itemLabel ? ` (already used for ${other})` : ""}
            </option>
          );
        })}
      </Form.Select>
      {missing && (
        <Form.Control.Feedback type="invalid">
          The Excel you chose has no column "{value}". Pick the right one, or choose "The bank doesn't send this".
        </Form.Control.Feedback>
      )}
    </>
  );
};

// Add / edit a product and its Excel column setup
const ProductFormModal = ({ show, bank, product, allProducts, banks, uploadFields, onClose, onSaved }) => {
  const isEdit = Boolean(product);

  const [name, setName] = useState("");
  const [detailType, setDetailType] = useState("LOAN");
  const [dateFormat, setDateFormat] = useState(DEFAULT_DATE_FORMAT);
  const [mapping, setMapping] = useState({});
  const [sampleHeaders, setSampleHeaders] = useState([]);
  // How many fields the last sample Excel filled (null until one is picked)
  const [sampleFilled, setSampleFilled] = useState(null);
  // Product whose columns were copied, shown in the "Copy columns" dropdown
  const [copiedFromId, setCopiedFromId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Word formats of the product's documents (all required for a new product)
  const [formats, setFormats] = useState({ selection: {}, blocking: "Loading the document list…" });
  // What the bank's and product's Word files print, per field: { required, documents } (the LOT upload uses the same rule)
  const [needs, setNeeds] = useState({ fields: {}, unavailable: [] });

  useEffect(() => {
    if (!show) return;
    setError("");
    setSampleHeaders([]);
    setSampleFilled(null);
    setCopiedFromId("");
    setName(product?.Product_name || "");
    setDetailType(product?.detailType || "LOAN");
    setDateFormat(product?.dateFormat || DEFAULT_DATE_FORMAT);
    setMapping(
      Object.fromEntries(
        (product?.columns || []).map((c) => [c.field, { excelColumn: c.excelColumn, required: c.required }]),
      ),
    );
  }, [show, product]);

  const selectionKey = JSON.stringify(formats.selection);
  useEffect(() => {
    if (!show || !bank) return;
    let current = true;
    fetch(`${API_BASE_URL}/api/products/field-needs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ Client_id: bank.Client_id, Product_id: product?.Product_id, detailType, templates: formats.selection }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readErrorMessage(res));
        const result = await res.json();
        if (current) setNeeds({ fields: Object.fromEntries(result.fields.map((f) => [f.field, f])), unavailable: result.unavailable });
      })
      .catch((err) => current && setError(`Couldn't read what the Word files need: ${err.message}`));
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, bank, product, detailType, selectionKey]);
  const neededBy = (key) => needs.fields[key]?.documents || [];
  const neededForEveryCase = (key) => Boolean(needs.fields[key]?.required);

  const fields = useMemo(
    () =>
      (uploadFields?.fields || []).filter(
        (f) => f.group === "CASE" || f.group === "BORROWER" || f.group === detailType,
      ),
    [uploadFields, detailType],
  );
  const groups = ["CASE", "BORROWER", detailType];

  const setColumn = (field, patch) =>
    setMapping((prev) => ({
      ...prev,
      [field]: { excelColumn: "", required: false, ...prev[field], ...patch },
    }));

  const mappedHeaders = new Set(
    Object.values(mapping)
      .map((m) => simplify(m.excelColumn || ""))
      .filter(Boolean),
  );
  const unusedHeaders = sampleHeaders.filter((h) => !mappedHeaders.has(simplify(h)));
  const mappedCount = fields.filter((f) => mapping[f.key]?.excelColumn?.trim()).length;
  // The names the dropdowns offer: the chosen Excel's columns, then names already in the setup that it doesn't have
  const filledNames = fields.map((f) => mapping[f.key]?.excelColumn?.trim()).filter(Boolean);
  const choices = [...new Map([...sampleHeaders, ...filledNames].map((h) => [simplify(h), h])).values()];
  // Which item each of the bank's columns is already matched with (shown in the dropdowns)
  const usedFor = new Map(
    fields.filter((f) => mapping[f.key]?.excelColumn?.trim()).map((f) => [simplify(mapping[f.key].excelColumn), f.label]),
  );

  // Read the header row of a sample Excel; fill an empty field only when a header is the field's full name (case, spaces
  // and punctuation aside), never by a similar or internal name. Every link made is listed so it can be checked.
  const handleSampleExcel = (e) => {
    const file = e.target.files[0];
    e.target.value = ""; // picking the same file again re-reads it
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = XLSX.read(new Uint8Array(event.target.result), {
        type: "array",
        sheetRows: 1,
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const [headerRow = []] = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
      });
      const headers = headerRow.map((h) => String(h).trim()).filter(Boolean);
      setSampleHeaders(headers);

      const byName = new Map(headers.map((h) => [simplify(h), h]));
      const next = { ...mapping };
      const linked = [];
      fields.forEach((f) => {
        if (next[f.key]?.excelColumn?.trim()) return;
        const match = byName.get(simplify(f.label));
        if (match) {
          next[f.key] = { required: false, ...next[f.key], excelColumn: match };
          linked.push(`${match} → ${f.label}`);
        }
      });
      setMapping(next);
      setSampleFilled(linked);
    };
    reader.readAsArrayBuffer(file);
  };

  // Other products whose setup can be copied as a starting point (e.g. a similar Excel from another bank)
  const bankName = (clientId) => banks.find((b) => b.Client_id === clientId)?.client_name || "";
  const copySources = allProducts.filter((p) => p.isConfigured && p.Product_id !== product?.Product_id);

  const copyFromProduct = async (e) => {
    const sourceId = e.target.value;
    if (!sourceId) return;
    if (
      Object.values(mapping).some((m) => m.excelColumn?.trim()) &&
      !window.confirm("Replace the current column setup?")
    ) {
      return;
    }
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/${sourceId}`);
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const source = await res.json();
      setDetailType(source.detailType);
      setDateFormat(source.dateFormat);
      setMapping(
        Object.fromEntries(source.columns.map((c) => [c.field, { excelColumn: c.excelColumn, required: c.required }])),
      );
      setCopiedFromId(sourceId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSave = async () => {
    setError("");
    if (!name.trim()) {
      setError("Product name is required");
      return;
    }
    const missing = fields.filter((f) => (f.mandatory || neededForEveryCase(f.key)) && !mapping[f.key]?.excelColumn?.trim());
    if (missing.length) {
      setError(
        `Choose the bank's column for:\n${missing
          .map((f) => (f.mandatory ? f.label : `${f.label} (printed in the ${neededBy(f.key).join(", ")})`))
          .join("\n")}`,
      );
      return;
    }
    if (needs.unavailable.length) {
      setError(
        `The Word files print details this type of Excel can't have:\n${needs.unavailable
          .map((u) => `${u.label} (in the ${u.documents.join(", ")})`)
          .join("\n")}\nChange the Word file or the type of details.`,
      );
      return;
    }
    if (formats.blocking) {
      setError(formats.blocking);
      return;
    }

    const body = {
      Product_name: name.trim(),
      detailType,
      dateFormat,
      columns: fields
        .filter((f) => mapping[f.key]?.excelColumn?.trim())
        .map((f) => ({
          field: f.key,
          excelColumn: mapping[f.key].excelColumn.trim(),
          required: Boolean(f.mandatory || mapping[f.key].required),
        })),
      templates: formats.selection,
    };
    if (!isEdit) body.Client_id = bank.Client_id;

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/products${isEdit ? `/${product.Product_id}` : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop="static" scrollable>
      <Modal.Header closeButton closeVariant="white" className="customModal">
        <Modal.Title className="mx-auto" style={{ fontSize: "16px" }}>
          {isEdit ? "Edit Product" : "Add Product"} · {bank?.client_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: "12px" }}>
        {error && <Alert variant="danger" style={{ whiteSpace: "pre-line" }}>{error}</Alert>}

        <div className="row g-3">
          <div className="col-md-4">
            <Form.Label>Product name *</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Education Loan"
              style={{ fontSize: "12px" }}
            />
          </div>
          <div className="col-md-4">
            <Form.Label>Type of details in the Excel *</Form.Label>
            <Form.Select
              value={detailType}
              onChange={(e) => setDetailType(e.target.value)}
              style={{ fontSize: "12px" }}
            >
              <option value="LOAN">Loan</option>
              <option value="VEHICLE">Vehicle</option>
            </Form.Select>
          </div>
          <div className="col-md-4">
            <Form.Label>Date format used in the Excel *</Form.Label>
            <Form.Select
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value)}
              style={{ fontSize: "12px" }}
            >
              {(uploadFields?.dateFormats || []).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        <hr />
        <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
          <div>
            <Form.Label className="mb-1">Step 1: Choose one of the bank's LOT Excels</Form.Label>
            <Form.Control type="file" accept=".xlsx,.xls" onChange={handleSampleExcel} style={{ fontSize: "12px" }} />
          </div>
          {copySources.length > 0 && (
            <div>
              <Form.Label className="mb-1">Copy columns from another product (optional)</Form.Label>
              <Form.Select value={copiedFromId} onChange={copyFromProduct} style={{ fontSize: "12px" }}>
                <option value="">Choose a product...</option>
                {copySources.map((p) => (
                  <option key={p.Product_id} value={p.Product_id}>
                    {bankName(p.Client_id)} · {p.Product_name}
                  </option>
                ))}
              </Form.Select>
            </div>
          )}
          <span className="ms-auto text-muted">
            {mappedCount} of {fields.length} items matched
          </span>
        </div>

        {sampleFilled !== null && (
          <Alert variant={unusedHeaders.length ? "warning" : "success"} className="py-2">
            {sampleHeaders.length === 0 ? (
              "No column names found in row 1 of that Excel."
            ) : (
              <>
                {sampleFilled.length
                  ? `Filled ${sampleFilled.length} box${sampleFilled.length === 1 ? "" : "es"} where the bank's column has exactly the same name. Please check them: ${sampleFilled.join("; ")}. `
                  : "No column of the bank has exactly the same name as an item, so nothing was filled. Pick them in the boxes below. "}
                {unusedHeaders.length
                  ? `Not used yet: ${unusedHeaders.join(", ")}. Pick them in the boxes below if needed; any left over are skipped at upload.`
                  : "Every column of the bank's Excel is used."}
              </>
            )}
          </Alert>
        )}

        <Alert variant="info" className="py-2 mb-2">
          <strong>Step 2: Match each item on the left with the bank's column name.</strong> You are not entering customer
          details here, only telling the app which column of the bank's Excel holds each item.
          <ul className="mb-0 mt-1 ps-3">
            <li>Choose "The bank doesn't send this" for an item the bank's Excel doesn't have.</li>
            <li>
              Items with a red <span className="text-danger">*</span> are printed in the letters, so the bank's Excel must
              have them for every customer.
            </li>
            <li>Columns of the bank's Excel that aren't chosen here are skipped at upload.</li>
            <li>The bank's own name and addresses come from the bank's details, never from the Excel.</li>
            <li>One row per person: rows with the same Reference No are one case (first row borrower, then co-borrowers).</li>
          </ul>
          {sampleHeaders.length === 0 && (
            <div className="mt-1">
              The lists show the names already saved. To pick from all of the bank's columns, choose one of the bank's
              Excels (or this product's LOT template) in Step 1.
            </div>
          )}
        </Alert>

        {groups.map((group) => (
          <Table key={group} size="sm" bordered className="mb-3 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "35%" }}>{GROUP_TITLES[group]}</th>
                <th>Bank's column name</th>
                <th style={{ width: "90px" }} className="text-center">
                  Required
                </th>
              </tr>
            </thead>
            <tbody>
              {fields
                .filter((f) => f.group === group)
                .map((f) => (
                  <tr key={f.key}>
                    <td>
                      {f.label}
                      {(f.mandatory || neededForEveryCase(f.key)) && <span className="text-danger"> *</span>}
                      {f.type !== "text" && (
                        <Badge bg="light" text="dark" className="ms-2 border">
                          {f.type}
                        </Badge>
                      )}
                      {f.hint && (
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          {f.hint}
                        </div>
                      )}
                      {neededBy(f.key).length > 0 && (
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          {neededForEveryCase(f.key) ? "Printed for every case in" : "Printed in some cases in"}: {neededBy(f.key).join(", ")}
                        </div>
                      )}
                    </td>
                    <td>
                      <ColumnPicker
                        value={mapping[f.key]?.excelColumn || ""}
                        choices={choices}
                        fromExcel={sampleHeaders.length ? sampleHeaders : null}
                        usedFor={usedFor}
                        itemLabel={f.label}
                        onChange={(excelColumn) => setColumn(f.key, { excelColumn })}
                      />
                    </td>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={Boolean(f.mandatory || neededForEveryCase(f.key) || mapping[f.key]?.required)}
                        disabled={f.mandatory || neededForEveryCase(f.key) || !mapping[f.key]?.excelColumn?.trim()}
                        onChange={(e) => setColumn(f.key, { required: e.target.checked })}
                        aria-label={`${f.label} required`}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        ))}

        <hr />
        {show && bank && (
          <DocumentFormats
            level="product"
            owner={isEdit ? { clientId: product.Client_id, productId: product.Product_id } : null}
            onChange={setFormats}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {formats.blocking && (
          <div className="w-100 text-danger" style={{ fontSize: "12px" }}>
            Can't save yet: {formats.blocking}
          </div>
        )}
        <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <button
          className="custBtn"
          style={{ fontSize: "12px" }}
          onClick={handleSave}
          disabled={saving || Boolean(formats.blocking)}
          title={formats.blocking || undefined}
        >
          {saving ? "Saving..." : "Save Product"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductFormModal;
